/**
 * @description Stored card selector for vaulted payment cards.
 * Gateway-agnostic <select> dropdown that lets users pick a previously
 * saved card or revert to manual card entry (HPF). Used alongside CardEntry
 * in payment flows.
 *
 * Key features:
 * - Fetches customer's stored cards on mount via paymentSessionService
 * - Single-select dropdown showing brand, last 4, expiry, and default badge
 * - "Use a new card" button appears ONLY after a wallet card is selected,
 *   giving users a way to revert to HPF without first picking a different card
 * - Set default / Remove actions (showActions=true only) appear inline next
 *   to the dropdown when a card is selected
 * - Loading, empty, and error states
 *
 * @dependencies
 * - paymentSessionService: getCustomerCards(), setDefaultCard(), removeCard()
 *
 * @notes
 * - customerId is a PostgreSQL INTEGER (not Wristband ID, not public ID)
 * - Card data is already masked by the backend (last4, brand only — no PCI concern)
 * - The parent component decides what to do with the selected card
 *   (e.g., pass storedCardId to GatewayService.processPayment)
 * - This component does NOT handle payment submission — it only manages selection
 * - Default state (no card selected) shows the dropdown with a placeholder and
 *   nothing else — the parent's HPF form is the implicit "new card" entry point
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { paymentSessionService } from '../../services/api';

function StoredCardSelector({
  customerId,
  onSelect,
  onNewCard,
  selectedCardId = null,
  disabled = false,
  label = 'Saved Cards',
  showActions = true
}) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingCardId, setRemovingCardId] = useState(null);
  const [settingDefaultId, setSettingDefaultId] = useState(null);
  const fetchedRef = useRef(false);

  // Fetch stored cards for this customer
  const fetchCards = useCallback(async () => {
    if (!customerId) {
      setCards([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await paymentSessionService.getCustomerCards(customerId);
      setCards(data.cards || []);
    } catch (err) {
      console.error('Failed to fetch stored cards:', err);
      setError('Unable to load saved cards');
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    // Guard against double-fetch (React StrictMode)
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchCards();
  }, [fetchCards]);

  // Refetch when customerId changes (e.g., customer selector in PaymentRecorder)
  useEffect(() => {
    if (fetchedRef.current && customerId) {
      fetchCards();
    }
  }, [customerId, fetchCards]);

  // Set a card as default
  const handleSetDefault = async (cardId) => {
    try {
      setSettingDefaultId(cardId);
      await paymentSessionService.setDefaultCard(cardId, customerId);

      // Update local state — mark new default, unmark old
      setCards(prev => prev.map(card => ({
        ...card,
        isDefault: card.id === cardId
      })));
    } catch (err) {
      console.error('Failed to set default card:', err);
    } finally {
      setSettingDefaultId(null);
    }
  };

  // Remove a stored card
  const handleRemove = async (cardId) => {
    try {
      setRemovingCardId(cardId);
      await paymentSessionService.removeCard(cardId);

      // Remove from local state
      setCards(prev => prev.filter(card => card.id !== cardId));

      // If the removed card was selected, clear selection
      if (selectedCardId === cardId) {
        onSelect?.(null);
      }
    } catch (err) {
      console.error('Failed to remove card:', err);
    } finally {
      setRemovingCardId(null);
    }
  };

  // Card brand icon placeholder (can be swapped for actual SVG icons later)
  const getBrandDisplay = (brand) => {
    const brands = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'Amex',
      discover: 'Discover',
      diners: 'Diners',
      jcb: 'JCB'
    };
    return brands[brand?.toLowerCase()] || brand || 'Card';
  };

  // Format a single card for display in the <option> label
  const formatCardOption = (card) => {
    const brand = getBrandDisplay(card.cardBrand);
    const expiry = card.expiryMonth && card.expiryYear
      ? ` — ${String(card.expiryMonth).padStart(2, '0')}/${String(card.expiryYear).slice(-2)}`
      : '';
    const defaultMark = card.isDefault ? ' (Default)' : '';
    return `${brand} ****${card.lastFour}${expiry}${defaultMark}`;
  };

  // Handle dropdown change — empty value clears the selection
  const handleSelectChange = (e) => {
    const value = e.target.value;
    if (!value) {
      onSelect?.(null);
      return;
    }
    const card = cards.find(c => c.id === value) || null;
    onSelect?.(card);
  };

  // ========================================================================
  // Render
  // ========================================================================

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
        <svg className="animate-spin h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Loading saved cards...
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-sm text-gray-500 py-3">
        <p>{error}</p>
        <button
          onClick={() => { fetchedRef.current = false; fetchCards(); }}
          className="text-indigo-600 hover:text-indigo-700 text-sm mt-1"
        >
          Try again
        </button>
      </div>
    );
  }

  // No saved cards
  if (cards.length === 0) {
    return null; // Don't render anything — parent will show CardEntry directly
  }

  const selectedCard = selectedCardId ? cards.find(c => c.id === selectedCardId) : null;

  return (
    <div className="space-y-3">
      <label htmlFor="stored-card-select" className="block text-sm font-medium text-gray-700">
        {label}
      </label>

      {/* Dropdown — single source of truth for selection */}
      <select
        id="stored-card-select"
        value={selectedCardId || ''}
        onChange={handleSelectChange}
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <option value="">Select a saved card…</option>
        {cards.map(card => (
          <option key={card.id} value={card.id}>
            {formatCardOption(card)}
          </option>
        ))}
      </select>

      {/* Per-card actions — only when a card is selected and showActions is enabled.
          Hidden via showActions={false} from PaymentRecorder to avoid accidental
          wallet management during a payment. Card management lives on Customer Details. */}
      {showActions && selectedCard && (
        <div className="flex items-center gap-3 text-sm">
          {!selectedCard.isDefault && (
            <button
              type="button"
              onClick={() => handleSetDefault(selectedCard.id)}
              disabled={disabled || settingDefaultId === selectedCard.id}
              className="text-gray-600 hover:text-indigo-600 disabled:opacity-50"
            >
              {settingDefaultId === selectedCard.id ? 'Setting…' : 'Set as default'}
            </button>
          )}
          <button
            type="button"
            onClick={() => handleRemove(selectedCard.id)}
            disabled={disabled || removingCardId === selectedCard.id}
            className="text-red-600 hover:text-red-700 disabled:opacity-50"
          >
            {removingCardId === selectedCard.id ? 'Removing…' : 'Remove'}
          </button>
        </div>
      )}

      {/* "Use a new card" — only shown after a wallet card is selected, so the
          user can back out to HPF entry. Hidden in the default state since the
          parent's HPF form is already the implicit new-card entry point. */}
      {selectedCardId && onNewCard && (
        <button
          type="button"
          onClick={onNewCard}
          disabled={disabled}
          className="w-full flex items-center justify-center gap-2 p-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Use a new card
        </button>
      )}
    </div>
  );
}

export default StoredCardSelector;
